// RewardsTrTable.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Box,
  ListItemText,
  Typography,
  MenuItem,
  Select,
  Checkbox,
  TableSortLabel,
  ListSubheader,
  FormControl,
} from "@mui/material";
import { exportTranData, transactionViewRewards } from "../api";
import { getToken } from "../../../utils/utils";
import PaginationReports from "../../brands/PaginationReports";
import { useDebounce } from "use-debounce";
import Export from "../../../assests/images/export.svg";
import * as XLSX from "xlsx";
import Sorting from "../../../assests/images/Sorting.svg";

const CustomArrow: React.FC<{ open: boolean; onClick: () => void }> = ({ open, onClick }) => (
  <span
    onClick={(e) => {
      e.stopPropagation(); // prevent Select double-firing
      onClick();
    }}
    style={{
      display: "inline-block",
      width: 11,
      right: "10px",
      height: "8px",
      borderRight: "2px solid #000",
      borderBottom: "2px solid #000",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transition: "transform 0.2s ease",
      marginLeft: "20px",
      position: "relative",
      cursor: "pointer",
      pointerEvents: "auto",
    }}
  />
);

// Define interfaces (kept as in your original)
export interface RewardPointResponse {
  region: string;
  asm_name: string;
  area_code: string;
  tse_name: string;
  territory_code: string;
  pmi_entity_code: string;
  user_first_name: string;
  user_last_name: string;
  outlet_name: string;
  outlet_category: string;
  city: string;
  phone_number: string;
  member_since: string;
  transaction_date: string;
  transaction_type: string;
  transaction_detail: string;
  redemption_request_date: string;
  redemption_status: string;
  redemption_approval_rejection_date: string;
  points: number;
  contact_id: string;
}

export interface RewardPointRequest {
  page: number;
  limit: number;
  filters: {
    city?: string[] | null;
    outlet_category?: string[] | null;
    transaction_type?: string[] | null;
    redemption_status?: string[] | null;
    fromdate?: string | null;
    todate?: string | null;
    outlet_name?: string[] | null; // <-- already present
    memberSinceStart?: string | null;
    memberSinceEnd?: string | null;
  };
  search_key?: string | null;
  start?: string | null;
  end?: string | null;
  sort?: string | { field: string; direction: "asc" | "desc" } | null;
  type?: string | undefined;
}

interface ExpandableTableRowProps {
  row: RewardPointResponse;
  selectedredemptions: string[];
  visibleColumns: string[];
  columnKeyMap: Record<string, keyof RewardPointResponse | string>;
  contactidmain: string;
}
const ExpandableTableRow = ({ row, visibleColumns, columnKeyMap }: ExpandableTableRowProps) => (
  <>
    <TableRow>
      {visibleColumns.map((col, index) => (
        <TableCell
          key={col}
          component={index === 0 ? "th" : undefined}
          scope={index === 0 ? "row" : undefined}
        >
          {row[columnKeyMap[col] as keyof RewardPointResponse] ?? "-"}
        </TableCell>
      ))}
    </TableRow>
  </>
);

// Filters shape kept internal
interface Filters {
  [key: string]: { [key: string]: boolean };
}

const RewardsTrTable = ({
  startDate,
  endDate,
  searchQuery,
  sortOption,
  appliedFilters,
  onBackendFilterOptions,
}: any) => {
  const [users, setUsers] = useState<RewardPointResponse[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedtransactions, setselectedtransactions] = useState<string[]>([]);
  const [selectedredemptions, setselectedredemptions] = useState<string[]>([]);
  const [selectedOutletTypes, setSelectedOutletTypes] = useState<string[]>([]);

  // ✅ NEW: outlet names state
  const [selectedOutletNames, setSelectedOutletNames] = useState<string[]>([]);

  const [page, setPage] = useState(1);
  const [contactidmain, setcontactidmain] = useState<string[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [memberSinceRange, setMemberSinceRange] = useState<[Date | null, Date | null]>([null, null]);

  const [orderBy, setOrderBy] = useState<keyof RewardPointResponse | string>("user_first_name");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [colSelectOpen, setColSelectOpen] = useState(false);

  const token = getToken();
  const [filters, setFilters] = useState<Filters>({
    locations: { all: true },
    pointsTransaction: { all: true },
    redemptionRequest: { all: true },
  });

  const [debouncedSearchTerm] = useDebounce(searchQuery, 500);
  const itemsPerPage = 10;

  const allColumns = [
    "First Name",
    "Last Name",
    "Outlet Name",
    "City",
    "Outlet Category",
    "Transaction Date",
    "Member Since",
    "Region",
    "ASM Name",
    "TSE Name",
    "Territory Code",
    "Phone Number",
    "Transaction Type",
    "Transaction Detail",
    "Redemption Request Date",
    "Redemption Status",
    "Redemption Approval/Rejection Date",
    "Points",
  ];

  const columnKeyMap: Record<string, keyof RewardPointResponse | string> = {
    "First Name": "user_first_name",
    "Last Name": "user_last_name",
    "Outlet Name": "outlet_name",
    "Outlet Category": "outlet_category",
    Region: "region",
    "ASM Name": "asm_name",
    "TSE Name": "tse_name",
    "Territory Code": "territory_code",
    City: "city",
    "Phone Number": "phone_number",
    "Member Since": "member_since",
    "Transaction Date": "transaction_date",
    "Transaction Type": "transaction_type",
    "Transaction Detail": "transaction_detail",
    "Redemption Request Date": "redemption_request_date",
    "Redemption Status": "redemption_status",
    "Redemption Approval/Rejection Date": "redemption_approval_rejection_date",
    Points: "points",
  };

  // Defaults (first 6 like the other tables)
  const DEFAULT_VISIBLE = allColumns.slice(0, 6);
  const DEFAULT_SET = new Set<string>(DEFAULT_VISIBLE);

  // Restore defaults if empty
  const withDefaultsIfEmpty = (arr: string[]) => (arr && arr.length ? arr : [...DEFAULT_VISIBLE]);

  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE);
  const [tempSelectedColumns, setTempSelectedColumns] = useState<string[]>(DEFAULT_VISIBLE);

  // column search & select-all
  const [colSearch, setColSearch] = useState<string>("");
  const colSearchRef = useRef<HTMLInputElement | null>(null);

  const filteredColumns = React.useMemo(() => {
    const q = (colSearch || "").trim().toLowerCase();
    if (!q) return allColumns;
    return allColumns.filter((c) => c.toLowerCase().includes(q));
  }, [allColumns, colSearch]);

  const allFilteredSelected =
    filteredColumns.length > 0 && filteredColumns.every((c) => tempSelectedColumns.includes(c));

  const toggleSelectAllOnFiltered = () => {
    if (allFilteredSelected) {
      // Deselect only non-defaults in the filtered set
      setTempSelectedColumns((prev) => {
        const next = prev.filter((p) => !filteredColumns.includes(p) || DEFAULT_SET.has(p));
        // ensure defaults stay present
        DEFAULT_VISIBLE.forEach((d) => {
          if (!next.includes(d)) next.push(d);
        });
        return next;
      });
    } else {
      // Select all filtered + keep defaults selected
      setTempSelectedColumns((prev) =>
        Array.from(new Set<string>([...prev, ...filteredColumns, ...DEFAULT_VISIBLE]))
      );
    }
  };

  useEffect(() => {
    if (!appliedFilters) {
      setSelectedLocations([]);
      setSelectedOutletTypes([]);
      setselectedtransactions([]);
      setselectedredemptions([]);
      setSelectedOutletNames([]); // ✅ reset
      setDateRange([null, null]);
      setMemberSinceRange([null, null]);
      return;
    }

    // accept both top-level and backendFilters.* variants (defensive)
    const {
      locations,
      outletTypes,
      transactionTypes,
      redemptionStatuses,
      lastloginStart,
      lastloginEnd,
      memberSinceStart,
      memberSinceEnd,
      backendFilters,
    } = appliedFilters as any;

    setSelectedLocations(Array.isArray(locations) ? locations : []);
    setSelectedOutletTypes(Array.isArray(outletTypes) ? outletTypes : []);
    setselectedtransactions(Array.isArray(transactionTypes) ? transactionTypes : []);
    setselectedredemptions(Array.isArray(redemptionStatuses) ? redemptionStatuses : []);

    // ✅ hydrate outlet names (accepts several shapes)
    const outletNamesTop =
      (appliedFilters as any).outletNames ??
      (appliedFilters as any).outlet_name ??
      (backendFilters && backendFilters.outlet_name) ??
      [];
    setSelectedOutletNames(Array.isArray(outletNamesTop) ? outletNamesTop : []);

    setDateRange([
      lastloginStart ? new Date(lastloginStart) : null,
      lastloginEnd ? new Date(lastloginEnd) : null,
    ]);

    // prefer backendFilters values when present (parent might nest filters there)
    const memStartRaw =
      (backendFilters && backendFilters.memberSinceStart) ??
      memberSinceStart ??
      (appliedFilters.memberSinceStart ?? null);
    const memEndRaw =
      (backendFilters && backendFilters.memberSinceEnd) ??
      memberSinceEnd ??
      (appliedFilters.memberSinceEnd ?? null);

    const memStart = memStartRaw ? new Date(memStartRaw) : null;
    const memEnd = memEndRaw ? new Date(memEndRaw) : null;
    setMemberSinceRange([memStart, memEnd]);

    setPage(1);
  }, [appliedFilters]);

  const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return "";
    const parsedDate = new Date(date);
    return `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, "0")}-${String(
      parsedDate.getDate()
    ).padStart(2, "0")}`;
  };

  const fetchRewardPointApi = async () => {
    const effectiveTxnStart = startDate ?? dateRange[0];
    const effectiveTxnEnd = endDate ?? dateRange[1];

    try {
      const reqBody: RewardPointRequest = {
        page,
        limit: itemsPerPage,
        filters: {
          city: selectedLocations.length ? selectedLocations : null,
          outlet_category: selectedOutletTypes.length ? selectedOutletTypes : null,
          transaction_type: selectedtransactions.length ? selectedtransactions : null,
          redemption_status: selectedredemptions.length ? selectedredemptions : null,
          outlet_name: selectedOutletNames.length ? selectedOutletNames : null, // ✅ send outlet names
          fromdate: effectiveTxnStart ? formatDate(effectiveTxnStart) : null,
          todate: effectiveTxnEnd ? formatDate(effectiveTxnEnd) : null,
          memberSinceStart: memberSinceRange[0] ? formatDate(memberSinceRange[0]) : null,
          memberSinceEnd: memberSinceRange[1] ? formatDate(memberSinceRange[1]) : null,
        },
        search_key: debouncedSearchTerm || null,
        sort: sortOption,
      };

      const res = await transactionViewRewards(reqBody, token);

      const apiData = res?.data || [];
      const mappedData = apiData.map((item: any) => ({
        region: item.region,
        asm_name: item.asm_name,
        area_code: item.area_code,
        tse_name: item.tse_name,
        territory_code: item.territory_code,
        pmi_entity_code: item.pmi_entity_code,
        user_first_name: item.user_first_name,
        user_last_name: item.user_last_name,
        outlet_name: item.outlet_name,
        outlet_category: item.outlet_category,
        city: item.city,
        phone_number: item.phone_number,
        member_since: item.member_since,
        transaction_date: item.transaction_date,
        transaction_type: item.transaction_type,
        transaction_detail: item.transaction_detail,
        redemption_request_date: item.redemption_request_date,
        redemption_status: item.redemption_status,
        redemption_approval_rejection_date: item.redemption_approval_rejection_date,
        points: Number(item.points) || 0,
        contact_id: item.contact_id,
      }));

      setcontactidmain(apiData.map((it: any) => it.contact_id));
      setUsers(mappedData || []);
      const totalItems = res?.totalCount || 0;
      setTotalRecords(totalItems);
      setTotalPages(Math.ceil(totalItems / itemsPerPage));

      if (onBackendFilterOptions && typeof onBackendFilterOptions === "function") {
        try {
          onBackendFilterOptions(res?.filterOptions ?? null);
        } catch (err) {
          console.warn("onBackendFilterOptions callback threw:", err);
        }
      }
    } catch (error) {
      console.error("Error fetching reward points:", error);
      setUsers([]);
      setTotalRecords(0);
      setTotalPages(1);
    }
  };

  useEffect(() => {
    fetchRewardPointApi();
  }, [
    startDate,
    endDate,
    selectedtransactions,
    selectedLocations,
    selectedOutletTypes,
    selectedOutletNames, // ✅ refetch when outlet names change
    dateRange,
    debouncedSearchTerm,
    page,
    sortOption,
    visibleColumns,
  ]);

  // Keep local transaction date range in sync with the parent DatePicker
  useEffect(() => {
    setDateRange([startDate ?? null, endDate ?? null]);
    setPage(1);
  }, [startDate, endDate]);

  const handleExportClick = async () => {
    try {
      // map visible column labels to backend field keys
      const selectedColumnKeys = visibleColumns.map((label) => columnKeyMap[label]).filter(Boolean) as string[];
      const effectiveTxnStart = startDate ?? dateRange[0];
      const effectiveTxnEnd = endDate ?? dateRange[1];

      const payload = {
        city: selectedLocations.length ? selectedLocations : null,
        outlet_category: selectedOutletTypes.length ? selectedOutletTypes : null,
        transaction_type: selectedtransactions.length ? selectedtransactions : null,
        redemption_status: selectedredemptions.length ? selectedredemptions : null,
        outlet_name: selectedOutletNames.length ? selectedOutletNames : null, // ✅ include in export
        fromdate: effectiveTxnStart ? formatDate(effectiveTxnStart) : null,
        todate: effectiveTxnEnd ? formatDate(effectiveTxnEnd) : null,
        search_key: debouncedSearchTerm || null,
        // optional: remove start/end if backend doesn't use them
        start: startDate ? formatDate(startDate) : null,
        end: endDate ? formatDate(endDate) : null,
        sort: sortOption ? (sortOption === "highest" ? "desc" : "asc") : null,
        page: 1,
        limit: 1000000,
        selectedColumns: selectedColumnKeys,
      };

      const blob = await exportTranData(payload, token);
      const data = await blob.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[workbook.SheetNames[0]]);

      const csvBlob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(csvBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "TRrewards-export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting CSV:", error);
    }
  };

  const handleSort = (field: keyof RewardPointResponse | string) => {
    if (orderBy === field) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setOrderBy(field);
      setOrder("asc");
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    const aVal = a[orderBy as keyof RewardPointResponse] ?? "";
    const bVal = b[orderBy as keyof RewardPointResponse] ?? "";
    const aStr = typeof aVal === "string" ? aVal : aVal?.toString();
    const bStr = typeof bVal === "string" ? bVal : bVal?.toString();
    if (aStr < bStr) return order === "asc" ? -1 : 1;
    if (aStr > bStr) return order === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <Box sx={{ background: "#fff", border: "1px solid #d1cfcf", borderRadius: "10px" }}>
      <Box sx={{ padding: "25px", display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "16px", fontWeight: "600", marginTop: "10px" }}>
          Total {totalRecords} Users
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ marginRight: "20px" }}>
            <FormControl sx={{ minWidth: 240, width: 240 }}>
              <Select
                multiple
                displayEmpty
                value={tempSelectedColumns}
                open={colSelectOpen}
                onOpen={() => setColSelectOpen(true)}
                onClose={() => {
                  setColSelectOpen(false);
                  const next = withDefaultsIfEmpty(tempSelectedColumns);
                  setTempSelectedColumns(next); // keep internal state valid
                  setVisibleColumns(next); // commit to table
                  setColSearch("");
                }}
                onChange={(e) => {
                  const val = e.target.value;
                  const arr = typeof val === "string" ? val.split(",") : (val as string[]);
                  if (Array.isArray(arr) && (arr as any[]).includes("__ALL__")) {
                    // handled by MenuItem onClick but keep safety
                    toggleSelectAllOnFiltered();
                    return;
                  }
                  const next = withDefaultsIfEmpty(Array.isArray(arr) ? arr : []);
                  setTempSelectedColumns(next);
                }}
                renderValue={(selected) => {
                  if ((selected as string[]).length === 0) return "Select Columns";
                  return (selected as string[]).join(", ");
                }}
                inputProps={{ "aria-label": "columns-select" }}
                // use the custom arrow (keeps arrow click handling isolated)
                IconComponent={() => <CustomArrow open={colSelectOpen} onClick={() => setColSelectOpen((v) => !v)} />}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      minWidth: 240,
                      paddingTop: 0,
                      paddingBottom: 0,
                      overflow: "overlay",
                      borderRadius: 10,
                      background: "#fff",
                      boxShadow: "none",
                    },
                  },
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  disableAutoFocusItem: true,
                  MenuListProps: {
                    style: {
                      paddingTop: 0,
                      paddingBottom: 0,
                      maxHeight: "250px",
                      overflowY: "auto",
                    },
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                        setTimeout(() => colSearchRef.current?.focus(), 0);
                      }
                    },
                  },
                }}
                sx={{
                  minWidth: 240,
                  height: "40px",
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  marginRight: "8px",
                  width: "240px",
                  position: "relative",

                  // hide the default MUI icon area (we're using custom arrow)
                  "& .MuiSelect-icon": {
                    display: "none",
                  },

                  // keep the select visually stable on hover/focus
                  "&:hover, &.Mui-focused, &.Mui-focusVisible": {
                    background: "transparent !important",
                    boxShadow: "none !important",
                    outline: "none !important",
                  },
                }}
              >
                <ListSubheader
                  sx={{
                    bgcolor: "white",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      px: 1,
                      py: 0.5,
                      border: "1px solid #ccc",
                      borderRadius: 1,
                      bgcolor: "#fafafa",
                    }}
                    onClick={(ev) => ev.stopPropagation()}
                  >
                    <input
                      ref={colSearchRef}
                      value={colSearch}
                      onChange={(e) => setColSearch(e.target.value)}
                      placeholder="Search columns..."
                      style={{
                        width: "100%",
                        border: "none",
                        outline: "none",
                        fontSize: 14,
                        background: "transparent",
                        padding: "4px",
                        height: "35px",
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                      }}
                    />
                    {colSearch && (
                      <Box
                        component="span"
                        sx={{
                          cursor: "pointer",
                          fontWeight: "bold",
                          fontSize: 18,
                          lineHeight: 1,
                          px: 1,
                          color: "gray",
                        }}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setColSearch("");
                          setTimeout(() => colSearchRef.current?.focus(), 0);
                        }}
                      >
                        ×
                      </Box>
                    )}
                  </Box>
                </ListSubheader>

                {/* Select All now sends a reserved value "__ALL__" which we intercept above */}
                <MenuItem
                  dense
                  value="__ALL__"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    // we toggle via the click as well (defensive)
                    toggleSelectAllOnFiltered();
                  }}
                >
                  <Checkbox checked={allFilteredSelected} />
                  <ListItemText primary="Select All" />
                </MenuItem>

                {filteredColumns.length ? (
                  filteredColumns.map((name) => (
                    <MenuItem key={name} value={name}>
                      <Checkbox checked={tempSelectedColumns.includes(name)} />
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled dense>
                    <ListItemText primary="No results" />
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>

          <Button
            variant="contained"
            sx={{
              textTransform: "capitalize",
              backgroundColor: "#fff",
              fontSize: "14px",
              fontWeight: "500",
              position: "relative",
              left: "-7px",
              height: "40px",
              padding: "16px",
              borderRadius: "10px",
              color: "#C8172F",
              border: "1px solid #C8172F",
              boxShadow: "none",
              "& .MuiButton-endIcon": { fontSize: "30px", color: "var(--Icon-Colour, #C8172F)" },
            }}
            startIcon={<img src={Export} alt="export" />}
            onClick={() => handleExportClick()}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{ borderTop: "1px solid #d3d3d3", boxShadow: "none", maxWidth: "100%", overflowX: "auto" }}
      >
        <Table sx={{ minWidth: visibleColumns.length * 150 }}>
          <TableHead sx={{ background: "#EEF4FF" }}>
            <TableRow>
              {visibleColumns.map((col) => (
                <TableCell key={col} sx={{ minWidth: 150 }}>
                  <TableSortLabel
                    active={orderBy === columnKeyMap[col]}
                    direction={orderBy === columnKeyMap[col] ? order : "asc"}
                    onClick={() => handleSort(columnKeyMap[col])}
                    IconComponent={() => <img src={Sorting} alt="sort" style={{ width: 16, height: 16 }} />}
                  >
                    {col}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedUsers.length > 0 ? (
              sortedUsers.map((row) => (
                <ExpandableTableRow
                  key={row.contact_id}
                  row={row}
                  visibleColumns={visibleColumns}
                  columnKeyMap={columnKeyMap}
                  selectedredemptions={[]}
                  contactidmain={""}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {users.length > 0 && (
        <PaginationReports page={page} limit={itemsPerPage} totalPages={totalPages} setPage={setPage} />
      )}
    </Box>
  );
};

export default RewardsTrTable;
